import React, { useEffect, useState } from 'react';
import './CompanyLogos.css';

const baseUrl = 'https://firebasestorage.googleapis.com/v0/b/otanertech.firebasestorage.app/o/ns-logos%2F';
const successfulSources = new Map();
const pending = new Map();
const formats = ['png', 'jpeg', 'jpg'];
const RETRY_MS = 4000;
const MAX_RETRIES = 4;

// One shared lookup per company, even when multiple cards request its logo.
function findLogo(symbol) {
  if (successfulSources.has(symbol)) return Promise.resolve(successfulSources.get(symbol));
  if (pending.has(symbol)) return pending.get(symbol);

  const urls = formats.map(ext => `${baseUrl}${encodeURIComponent(symbol)}.${ext}?alt=media`);
  const request = new Promise(resolve => {
    let remaining = urls.length;
    let settled = false;
    const images = [];
    const finish = url => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      images.forEach(img => { img.onload = null; img.onerror = null; });
      if (url) successfulSources.set(symbol, url);
      resolve(url);
    };
    const timeout = setTimeout(() => finish(null), 3500);
    urls.forEach(url => {
      const img = new Image();
      images.push(img);
      img.onload = () => { if (img.naturalWidth > 0) finish(url); else img.onerror(); };
      img.onerror = () => { remaining -= 1; if (!remaining) finish(null); };
      img.src = url;
      if (img.complete && img.naturalWidth > 0) finish(url);
    });
  });
  pending.set(symbol, request);
  request.finally(() => { if (pending.get(symbol) === request) pending.delete(symbol); });
  return request;
}

export default function CompanyLogos({ symbol }) {
  const companySymbol = symbol === 'T.TO' ? symbol : String(symbol || '').trim().replace(/\..*$/, '').toUpperCase();
  const [url, setUrl] = useState(() => successfulSources.get(companySymbol) || '');
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    let active = true;
    let timer;
    setUrl(successfulSources.get(companySymbol) || '');
    setExhausted(false);
    if (!companySymbol) return undefined;

    let retries = 0;
    const attempt = () => {
      findLogo(companySymbol).then(found => {
        if (!active) return;
        if (found) setUrl(found);
        else if (retries < MAX_RETRIES) {
          retries += 1;
          timer = setTimeout(attempt, RETRY_MS);
        } else setExhausted(true);
      });
    };
    attempt();
    return () => { active = false; clearTimeout(timer); };
  }, [companySymbol]);

  return (
    <span className={`company-logo-stage${url ? ' is-loaded' : ''}`} aria-busy={!url && !exhausted}>
      {!url && !exhausted && (
        <span className="company-logo-cosmos" aria-hidden="true">
          <span className="company-logo-cosmos-core" />
          <span className="company-logo-cosmos-orbit orbit-one" />
          <span className="company-logo-cosmos-orbit orbit-two" />
          <span className="company-logo-cosmos-spark spark-one" />
          <span className="company-logo-cosmos-spark spark-two" />
          <span className="company-logo-cosmos-spark spark-three" />
        </span>
      )}
      {!url && exhausted && <span className="company-logo-fallback" aria-label={`Logo unavailable for ${companySymbol}`}>{companySymbol}</span>}
      {url && <img src={url} alt={companySymbol || 'Company logo'} className="company-logos is-visible" />}
    </span>
  );
}
